/* eslint-disable react/no-danger */
import { IconButton, WhiteboardIcon } from "@minervaproject/fds";
import { MathJax } from "better-react-mathjax";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import { Pages } from "./controls/pages";
import { ResourceSettingsMenu } from "./controls/resource_settings_menu";
import { ResourceTitle } from "./controls/resource_title";
import { Zoom } from "./controls/zoom";
import { TldrawWhiteboard } from "./tldraw_whiteboard";

import { TooltipErrorBoundary } from "components/error_boundary";
import { useCapabilities } from "hooks/capabilities";
import { useAppSelector } from "hooks/redux_hooks";
import { ResourceType } from "shared/types";
import { classroomSelectors } from "store";

import "./html_slide_resource.scss";

const EMPTY_SLIDE =
  '<div class="html-slide-page-wrapper"><div class="resizing-html-slide-page-wrapper"></div></div>';
const MIN_MARGIN = 40;
const STANDARD_WIDTH = 900; // 11 wide
const STANDARD_HEIGHT = 695.5; // 8.5 tall

function HtmlSlideResource({ resource }: { resource: ResourceType }) {
  const { canControlClassroom, isVisibleInClass } = useCapabilities();
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const htmlSlideContent = resource.html_slide_content;
  const numberOfPages = htmlSlideContent.length;
  const [currentPage, setCurrentPage] = useState(resource.page || 1);
  const [defaultZoom, setDefaultZoom] = useState(1);
  const [zoom, setZoom] = useState(0);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [offsetTop, setOffsetTop] = useState(0);
  const [showWhiteboardControls, setShowWhiteboardControls] = useState(false);
  const currentSlideContent = useMemo(() => {
    const content = htmlSlideContent[currentPage - 1] || EMPTY_SLIDE;
    // Wrap all mathematical formulas in LaTeX delimiters
    return content.replace(
      /<span class="formula">(.*?)<\/span>/g,
      '<span class="formula">\\($1\\)</span>',
    );
  }, [htmlSlideContent, currentPage]);

  const slideContentRef = useRef<HTMLDivElement>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.flightLogger.log(`[HtmlSlideResource] change page ${resource.page}`);
    setCurrentPage(resource.page);
  }, [resource.page]);

  // Calculate the width and height of the slide
  useEffect(() => {
    if (slideContentRef.current) {
      const zoomToUse = zoom || defaultZoom;
      slideContentRef.current.style.minHeight = `${STANDARD_HEIGHT * zoomToUse}px`;
      slideContentRef.current.style.width = `${STANDARD_WIDTH * zoomToUse}px`;
      const resizingPageWrapperElement = slideContentRef.current.querySelector(
        ".resizing-html-slide-page-wrapper",
      ) as HTMLElement;
      if (resizingPageWrapperElement) {
        // @ts-expect-error https://github.com/microsoft/TypeScript/issues/49253
        resizingPageWrapperElement.style.zoom = zoomToUse;
      }
    }
  }, [currentPage, defaultZoom, zoom]);

  // Enable scrolling on the slide by temporarily disabling pointer events on the whiteboard
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const enableHtmlSlideScrolling = () => {
      if (whiteboardRef.current) {
        whiteboardRef.current.style.pointerEvents = "none";
      }

      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (whiteboardRef.current) {
          whiteboardRef.current.style.pointerEvents = "auto";
        }
      }, 500);
    };
    window.addEventListener("wheel", enableHtmlSlideScrolling);

    return () => {
      window.removeEventListener("wheel", enableHtmlSlideScrolling);
    };
  }, []);

  const _updateOffsets = () => {
    const slideContentElement = slideContentRef.current as HTMLElement;
    if (slideContentElement) {
      window.flightLogger.log(
        `[HtmlSlideResource] update offsets offsetLeft=${slideContentElement.offsetLeft} offsetTop=${slideContentElement.offsetTop} scrollLeft=${scrollRef.current?.scrollLeft} scrollTop=${scrollRef.current?.scrollTop}`,
      );
      setOffsetLeft(slideContentElement.offsetLeft - (scrollRef.current?.scrollLeft || 0));
      setOffsetTop(slideContentElement.offsetTop - (scrollRef.current?.scrollTop || 0));
    }
  };

  const updateOffsets = useMemo(() => debounce(_updateOffsets, 3), []);

  // Because the initial size of the slide is determined by the size of the container,
  // we need to calculate what level of zoom we're starting with (unlike PDFs, where
  // the zoom always defaults to 1).
  // The defaultZoom state is used to calculate the width and height of the slide until the
  // user manually zooms, at which point we switch over to using the zoom state.
  const updateDefaultZoom = ({ width, height }: DOMRectReadOnly) => {
    const availableWidth = width - MIN_MARGIN;
    const availableHeight = height - (MIN_MARGIN + 50); // HACK: 50 accounts for the whiteboard toolbar

    const zoomToFit = Math.min(
      Math.floor((availableWidth / STANDARD_WIDTH) * 10) / 10,
      Math.floor((availableHeight / STANDARD_HEIGHT) * 10) / 10,
    );

    if (zoomToFit > 0) {
      setDefaultZoom(zoomToFit);
    }
  };

  // Set up listeners and observers
  useEffect(() => {
    // For computing slide CSS
    const containerObserver = new ResizeObserver((entries) => {
      updateDefaultZoom(entries[0].contentRect);
      updateOffsets();
    });
    // For the whiteboard
    const slideContentObserver = new ResizeObserver(() => {
      updateOffsets();
    });

    const containerElement = containerRef.current;
    const slideContentElement = slideContentRef.current;
    const scrollElement = scrollRef.current;

    const onScroll = () => {
      updateOffsets();
    };

    if (containerElement) {
      containerObserver.observe(containerElement);
    }
    if (slideContentElement) {
      slideContentObserver.observe(slideContentElement);
    }
    if (scrollElement) {
      scrollElement.addEventListener("scroll", onScroll);
    }

    return () => {
      if (containerElement) {
        containerObserver.unobserve(containerElement);
      }
      if (slideContentElement) {
        slideContentObserver.unobserve(slideContentElement);
      }
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", onScroll);
      }
    };
  }, [updateOffsets]);

  return (
    <div className="flex flex-column w-full h-full" ref={containerRef}>
      <div className="flex items-center p1 bg-black body-s text-black-tint-70 resource-controls">
        <TooltipErrorBoundary text="The resource title cannot be displayed">
          <ResourceTitle resource={resource} />
        </TooltipErrorBoundary>
        <TooltipErrorBoundary text="The zoom buttons cannot be displayed">
          <Zoom zoom={zoom || defaultZoom} setZoom={setZoom} minZoom={0.5} />
        </TooltipErrorBoundary>
        {numberOfPages > 1 && (
          <TooltipErrorBoundary text="The navigation buttons cannot be displayed">
            <Pages
              resource={resource}
              numberOfPages={numberOfPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </TooltipErrorBoundary>
        )}
        {(canControlClassroom || studentsCanDraw) && isVisibleInClass && (
          <TooltipErrorBoundary text="The whiteboard controls cannot be displayed">
            <IconButton onClick={() => setShowWhiteboardControls(!showWhiteboardControls)}>
              <WhiteboardIcon width={16} height={16} color="var(--black-tint-70)" />
            </IconButton>
          </TooltipErrorBoundary>
        )}
        <TooltipErrorBoundary text="The resource settings menu cannot be displayed">
          <ResourceSettingsMenu editVerb="Draw" />
        </TooltipErrorBoundary>
      </div>

      <div className="flex flex-auto relative">
        <div className="flex-auto" style={{ overflow: "auto" }} ref={scrollRef}>
          <MathJax dynamic>
            <div
              className="html-slide-page-content mx-auto"
              dangerouslySetInnerHTML={{ __html: currentSlideContent }}
              ref={slideContentRef}
            />
          </MathJax>
        </div>
        <div
          className="absolute top-0 left-0"
          style={{ right: "20px", bottom: "20px" }} // HACK: 20px is approximately the width of a scrollbar
          ref={whiteboardRef}
        >
          <TldrawWhiteboard
            resource={resource}
            showControls={showWhiteboardControls}
            isOverlay
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            offsetLeft={offsetLeft}
            offsetTop={offsetTop}
            zoom={zoom || defaultZoom}
          />
        </div>
      </div>
    </div>
  );
}

export { HtmlSlideResource };
